import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    if (this.isOpen) {
      // The hamburger <summary> doubles as the close control. Left alone, clicking it
      // fires the native <details> toggle, which strips [open] immediately. The header
      // pinning (.header-section:has(...[open])) and the drawer's stacking depend on
      // [open], so losing it drops the panel behind the page and the close animation
      // plays on an already-hidden element. Prevent the native toggle so [open] persists
      // through the animation; reset() removes it once the roll-up finishes.
      if (event && typeof event.preventDefault === 'function') event.preventDefault();
      return this.close();
    }

    return this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    const finalize = () => {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    };

    // Mobile full-screen drawer: roll the panel back up into the header. We drive
    // this with the Web Animations API rather than a CSS keyframe/transition because
    // CSS refuses to (re)start the reverse animation when it replaces the forwards-
    // filling open animation in the same tick — the console proved the rule applies
    // (`animation-name: menu-roll-up`) but `animationstart` never fires, so the panel
    // snapped. `element.animate()` always runs and always resolves `finished`.
    if (drawer instanceof HTMLElement && drawer.classList.contains('menu-drawer') && this.#isMobileDrawer()) {
      const speed = getComputedStyle(drawer).getPropertyValue('--menu-slide-speed').trim();
      const parsed = parseFloat(speed);
      const duration = Number.isNaN(parsed) ? 500 : speed.endsWith('ms') ? parsed : parsed * 1000;

      const animation = drawer.animate([{ clipPath: 'inset(0 0 0 0)' }, { clipPath: 'inset(0 0 100% 0)' }], {
        duration,
        easing: 'ease-in-out',
      });

      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        finalize();
      };
      animation.addEventListener('finish', done, { once: true });
      animation.addEventListener('cancel', done, { once: true });
      return;
    }

    // Non-mobile / submenu close: wait for the CSS transition/animation to finish.
    this.#afterCloseTransition(drawer instanceof HTMLElement ? drawer : details, finalize);
  }

  /**
   * @returns {boolean} Whether the mobile (full-screen dropdown) drawer layout is active.
   */
  #isMobileDrawer() {
    return typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 749px)').matches;
  }

  /**
   * Run `callback` once the element's closing transition/animation has finished.
   * Falls back to a timeout derived from the element's computed durations so the
   * callback always fires even if no transition/animation event is dispatched.
   *
   * @param {HTMLElement} element
   * @param {() => void} callback
   */
  #afterCloseTransition(element, callback) {
    const styles = getComputedStyle(element);
    const toMs = (/** @type {string} */ value) =>
      value
        .split(',')
        .map((part) => {
          const trimmed = part.trim();
          const n = parseFloat(trimmed);
          if (Number.isNaN(n)) return 0;
          return trimmed.endsWith('ms') ? n : n * 1000;
        })
        .reduce((max, n) => Math.max(max, n), 0);

    const duration = Math.max(toMs(styles.transitionDuration), toMs(styles.animationDuration));

    if (duration <= 0) {
      callback();
      return;
    }

    let done = false;
    /** @param {Event} [event] */
    const finish = (event) => {
      // Ignore bubbled events from descendants; only the drawer's own end counts.
      if (event && event.target !== element) return;
      if (done) return;
      done = true;
      element.removeEventListener('transitionend', finish);
      element.removeEventListener('animationend', finish);
      callback();
    };

    element.addEventListener('transitionend', finish);
    element.addEventListener('animationend', finish);
    // Fallback: a little longer than the declared duration to absorb start latency.
    setTimeout(finish, duration + 80);
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
